import * as React from "react";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import type { WeekStartsOn } from "@/lib/dates/weekStart";
import { Label } from "@/components/ui/label";
import type { Matcher } from "react-day-picker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

type InputDatePickerProps = {
  id?: string;
  label?: string;
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minDate?: string;
  maxDate?: string;
  weekStartsOn?: WeekStartsOn;
};

const formatDate = (date?: Date) => {
  if (!date) {
    return "";
  }

  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const isValidDate = (date?: Date) => {
  if (!date) {
    return false;
  }
  return !Number.isNaN(date.getTime());
};

const padTwo = (value: number) => String(value).padStart(2, "0");
const toIsoDate = (date: Date) =>
  `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(
    date.getDate()
  )}`;
const parseIsoDate = (value?: string) =>
  value ? new Date(`${value}T00:00:00`) : undefined;

export function InputDatePicker({
  id,
  label,
  value,
  onChange,
  placeholder,
  minDate,
  maxDate,
  weekStartsOn,
}: InputDatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const selectedDate = React.useMemo(() => {
    const parsedValue = parseIsoDate(value);
    return isValidDate(parsedValue) ? parsedValue : undefined;
  }, [value]);
  const [month, setMonth] = React.useState<Date | undefined>(selectedDate);
  const minDateValue = minDate ? new Date(minDate) : undefined;
  const maxDateValue = maxDate ? new Date(maxDate) : undefined;
  const minDateValid = isValidDate(minDateValue);
  const maxDateValid = isValidDate(maxDateValue);
  const disabledMatchers: Matcher[] = [];

  if (minDateValid && minDateValue) {
    disabledMatchers.push({ before: minDateValue });
  }
  if (maxDateValid && maxDateValue) {
    disabledMatchers.push({ after: maxDateValue });
  }

  React.useEffect(() => {
    setMonth(selectedDate);
  }, [selectedDate]);

  const displayValue = selectedDate ? formatDate(selectedDate) : "";

  return (
    <div className="flex flex-col gap-3">
      {label && (
        <Label htmlFor={id} className="px-1">
          {label}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            id={id}
            className="w-full justify-between font-normal"
          >
            {displayValue || placeholder || "Select date"}
            <CalendarIcon className="size-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            captionLayout="label"
            month={month}
            onMonthChange={setMonth}
            weekStartsOn={weekStartsOn}
            disabled={disabledMatchers.length ? disabledMatchers : undefined}
            onSelect={(selectedDate) => {
              if (!selectedDate) {
                return;
              }
              onChange(toIsoDate(selectedDate));
              setMonth(selectedDate);
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
